import AcxSlider from "components/UI/AcxSlider";
import React, { useEffect } from "react";
import { IApplicationFilterComponent } from "../BaseFilterComponents/IApplicationFilterComponent";
import { observer } from "mobx-react";
import {
    MAX_CALL_DURATION,
    MIN_CALL_DURATION,
    resolveCallDurationSliderState,
} from "./callDurationSliderState";

interface CallDurationFilterProps extends IApplicationFilterComponent {
    label?: string;
    description?: string;
}

const MIN_DURATION = MIN_CALL_DURATION;
const MAX_DURATION = MAX_CALL_DURATION;

function clampDurationRange(
    duration: number[],
    minBound: number,
    maxBound: number,
    minSelectableMax: number,
): [number, number] {
    const rawMin =
        typeof duration[0] === "number" && Number.isFinite(duration[0])
            ? Math.max(MIN_DURATION, duration[0])
            : MIN_DURATION;
    const rawMax =
        typeof duration[1] === "number" && Number.isFinite(duration[1])
            ? Math.max(MIN_DURATION, duration[1])
            : MIN_DURATION;
    const isUnset = rawMin === MIN_DURATION && rawMax === MIN_DURATION;
    if (isUnset) {
        if (minSelectableMax === MIN_DURATION) {
            return [MIN_DURATION, MIN_DURATION];
        }

        const clampedMin = Math.max(minBound, rawMin);
        const clampedMax = Math.max(clampedMin, minSelectableMax);

        return [clampedMin, clampedMax];
    }

    const normalizedMax = rawMax === MIN_DURATION ? MAX_DURATION : rawMax;
    const clampedMin = Math.max(minBound, rawMin);
    const clampedMax = Math.max(
        minSelectableMax,
        clampedMin,
        Math.min(maxBound, normalizedMax),
    );

    return [clampedMin, clampedMax];
}

export const CallDurationFilter = observer(function (
    props: CallDurationFilterProps,
) {
    const minBound = Math.max(
        MIN_DURATION,
        props.rangeRestriction?.min ?? MIN_DURATION,
    );
    const maxBound = Math.min(
        MAX_DURATION,
        props.rangeRestriction?.max ?? MAX_DURATION,
    );
    const hasRestrictedSourceRange =
        props.rangeRestriction?.min !== undefined ||
        props.rangeRestriction?.max !== undefined;
    const minSelectableMax = hasRestrictedSourceRange
        ? Math.max(minBound, MIN_DURATION + 1)
        : MIN_DURATION;

    useEffect(() => {
        if (!props.rangeRestriction) return;
        const clamped = clampDurationRange(
            props.store.callDuration,
            minBound,
            maxBound,
            minSelectableMax,
        );
        if (
            clamped[0] === props.store.callDuration[0] &&
            clamped[1] === props.store.callDuration[1]
        ) {
            return;
        }

        props.store.setCallDuration(clamped);
    }, [
        maxBound,
        minBound,
        minSelectableMax,
        props.rangeRestriction,
        props.store,
        props.store.callDuration,
    ]);

    const unitLabel = props.description ?? "minutes";
    const { value: sliderValue, underSlideLabel } =
        resolveCallDurationSliderState(props.store.callDuration, {
            maxBound,
            unitLabel,
        });

    return (
        <AcxSlider
            label={props.label}
            description={unitLabel}
            value={sliderValue}
            underSlideLabel={underSlideLabel}
            onChange={(duration) => {
                // Dragging always stores a real range, including at the top of the track: 200 is
                // the highest bound the filter has, so "20 to 200" is what "20+" is underneath.
                // The parked thumb of a range with no stored bound settles onto the number it is
                // already sitting on, which means the same thing.
                props.store.setCallDuration(
                    clampDurationRange(
                        duration,
                        minBound,
                        maxBound,
                        minSelectableMax,
                    ),
                );
            }}
            minValue={minBound}
            maxValue={maxBound}
            noValueLabel="Any Duration"
        />
    );
});
